/**
 * useShellWalks - the moves this screen heard of lately, the newest per figure (plan design-gaps, M3).
 *
 * Read off the logs as the impacts are (engine/log/collect): the high-water marks start at mount, so a history
 * never replays, and each move is stamped `seenAt`, the moment THIS screen heard of it - the walk is timed from
 * there (engine/display/walk), so a late screen walks late rather than never. A move heard of only after it would
 * have ended is dropped. By actor: a newer move overtakes the one before. Pruned on a slow tick while any is kept.
 * Returns the kept moves by actor id. The line is flushed ahead of the token's write (useShellPings), so every
 * screen holds the walk before the figure's committed position moves.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { collectMoves } from '../engine/moveBeat';
import { WALK_KEEP_MS } from '../engine/display/walk';
import { noteSettled } from '../diagnostics/beatLog';

const NONE = {};

/** The fresh moves over the kept ones: the newest per actor, anything older than WALK_KEEP_MS gone. */
export function withFreshMoves(prev, fresh, now) {
  const next = {};
  for (const m of Object.values(prev)) if (now - m.seenAt <= WALK_KEEP_MS) next[m.actorId] = m;
  for (const m of fresh) if (playable(m, now) && newerThan(next[m.actorId], m)) next[m.actorId] = { ...m, seenAt: now };
  return next;
}

/** Heard of in time to play: a muted screen catching up on an old move plays nothing. */
const playable = (m, now) => now - m.at <= WALK_KEEP_MS;
/** A later move by the same figure overtakes the kept one; an older copy never does. */
const newerThan = (kept, m) => !kept || kept.at < m.at;

/** @param {object[]} tabs @param {object[]|undefined} firestoreNarrative */
export default function useShellWalks(tabs, firestoreNarrative) {
  const marks = useRef({ local: {}, shared: 0 });
  const [walks, setWalks] = useState(NONE);
  // A LAYOUT effect: the mover's own screen logs the line and writes the token in one tick, and the walk must be
  // in place before that render is painted, or the figure shows one frame at its destination first.
  useLayoutEffect(() => {
    const now = Date.now();
    const { moves, marks: next } = collectMoves(tabs, firestoreNarrative ?? [], marks.current, now);
    marks.current = next;
    // Heard of only after it would have ended: no walk, and the fuzzer's walk oracle is told so.
    for (const m of moves) if (!playable(m, now)) noteSettled(m.id, now);
    if (moves.length) setWalks((prev) => withFreshMoves(prev, moves, now));
  }, [tabs, firestoreNarrative]);
  useEffect(() => {
    if (!Object.keys(walks).length) return undefined;
    const t = setTimeout(() => setWalks((prev) => withFreshMoves(prev, [], Date.now())), WALK_KEEP_MS + 100);
    return () => clearTimeout(t);
  }, [walks]);
  return walks;
}
